import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedNestedSubAccount } from '../OrgChartContainer';
import styles from './OrgItem.module.scss';
import { withStyles, Box, MaterialSymbol, CircleButton, SubAccountBalanceView } from 'vopay';
import OrgDetails from '../OrgDetails/OrgDetails';
import BalanceDisplay from './BalanceDisplay';

import * as Popover from '@radix-ui/react-popover';

const classes = withStyles(styles);

type OrgItemProps = {
  node: ExtendedNestedSubAccount;
  canCreateAccountTransfer: boolean;
  displayBalance: SubAccountBalanceView;
  accountBlocked: boolean;
  canManageBlocking: boolean;
  onToggleExpand: (node: ExtendedNestedSubAccount) => void;
  onChangeMenuState: (open: boolean, node: ExtendedNestedSubAccount) => void;
  openCreateSubAccount: (node: ExtendedNestedSubAccount) => void;
  openAccountTransfer: (node: ExtendedNestedSubAccount) => void;
  onLocationChange: (location: string, state?: unknown) => void;
  onBalanceTypeChange: (nodeId: string, balanceType: SubAccountBalanceView) => void;
  onBlockAccount: (accountID: string, accountName: string) => void;
  onUnblockAccount: (accountID: string, accountName: string) => void;
};

export default function OrgItem({
  node,
  canCreateAccountTransfer,
  displayBalance,
  accountBlocked,
  canManageBlocking,
  onToggleExpand,
  onChangeMenuState,
  openAccountTransfer,
  openCreateSubAccount,
  onLocationChange,
  onBalanceTypeChange,
  onBlockAccount,
  onUnblockAccount,
}: OrgItemProps) {
  const { isExpanded, AccountName, numChildren, IsActive, isOpen, isBlurred } = node;
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.OrgItem' });

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      setOpen(isOpen);
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [isOpen]);

  return (
    <Popover.Root open={open}>
      <Popover.Trigger asChild>
        <div
          data-testid={`orgItem-${node.AccountID.replace(/\s+/g, '')}`}
          className={classes(
            'container',
            isExpanded && 'expanded',
            isOpen && 'active',
            isBlurred && 'blur',
            numChildren !== 0 && 'hasChildren',
          )}
          onClick={() => {
            if (numChildren !== 0) onToggleExpand(node);
          }}
        >
          <div className={classes('overlay')} />
          <div className={classes('overlayBlur')} />
          <div className={classes('orgItemBar', IsActive && node.AccountStatus !== 'Blocked' && 'active')}></div>

          <div className={classes('orgItem')}>
            {/* Top Section - Controls and Title */}
            <div className={classes('orgItemTop')}>
              <div className={classes('orgItemTopActions')}>
                <Box flx="row" jc="fs" gapSize="4px">
                  <CircleButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onChangeMenuState(true, node);
                    }}
                    icon="more_horiz"
                    size="small"
                    colorScheme="action"
                    className={classes('orgHoverWrapper')}
                  />

                  {node.IsActive && (
                    <CircleButton
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openCreateSubAccount(node);
                      }}
                      icon="add"
                      size="small"
                      colorScheme="action"
                      className={classes('orgHoverWrapper', 'addButton')}
                    />
                  )}
                </Box>

                <Box flx="row" jc="fe" maxheight="18px">
                  {numChildren !== 0 && (
                    <button
                      className={classes('orgItemExpand')}
                      aria-label={t('childrenToggle', {
                        name: AccountName,
                        defaultValue: 'Sub-accounts of {{name}}',
                      })}
                      aria-expanded={isExpanded}
                      onClick={() => onToggleExpand(node)}
                    >
                      {numChildren}{' '}
                      {isExpanded ? (
                        <MaterialSymbol icon="keyboard_arrow_up" />
                      ) : (
                        <MaterialSymbol icon="keyboard_arrow_down" />
                      )}
                    </button>
                  )}
                </Box>
              </div>

              <div className={classes('orgItemTitle')}>{AccountName}</div>
            </div>

            {/* Bottom Section - Balance Display */}
            <BalanceDisplay
              currency={node.Currency}
              availableBalance={node.AvailableBalance || '0'}
              rollupAvailableBalance={node.RollupAvailableBalance}
              nodeId={node.id}
              displayBalance={displayBalance}
              selectedBalanceType={
                node.selectedBalanceType ||
                (displayBalance === 'do-not-show-balances' ? 'available-balance' : displayBalance)
              }
              onBalanceTypeChange={onBalanceTypeChange}
            />
          </div>
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side={'right'}
          align={'end'}
          collisionPadding={{
            top: 75,
          }}
          sideOffset={15}
          data-popover-type="orgDetails"
        >
          <OrgDetails
            node={node}
            onClose={() => onChangeMenuState(false, node)}
            onLocationChange={onLocationChange}
            canCreateAccountTransfer={canCreateAccountTransfer}
            accountBlocked={accountBlocked}
            canManageBlocking={canManageBlocking}
            onBlockAccount={onBlockAccount}
            onUnblockAccount={onUnblockAccount}
            onAccountTransfer={() => {
              openAccountTransfer(node);
            }}
            onCreateSubAccount={() => {
              openCreateSubAccount(node);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
